import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { 
  fetchRequisitionDetails, 
  approveRequisitionAction, 
  rejectRequisitionAction, 
  clearSelectedRequisition, 
  clearRequisitionError, 
  RequisitionDetail, 
  addRequisitionCommentAction, 
  AddCommentPayload, 
  PictureItem 
} from './requisitionSlice';
import { AppDispatch, RootState } from '../../app/store';
import type { AuthUser } from '../auth/authSlice';
import { getStatusDisplay, StatusInfo } from '../../utils/statusUtils';
import EditATRModal from './EditATRModal';
import { formatCurrency } from '../../utils/formatUtils';
import { nl2br } from '../../utils/nl2br';

const RequisitionDetailPage = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Use Create React App's process.env for environment variables
  // Backend URL logging removed

  const currentUser = useSelector((state: RootState) => state.auth.user as AuthUser | null);

  // Use separate selectors to avoid memoization warning
  const selectedRequisition = useSelector((state: RootState) => state.requisitions.selectedRequisition as RequisitionDetail | null);
  const isLoading = useSelector((state: RootState) => state.requisitions.isLoadingDetails || state.requisitions.isUpdating);
  const error = useSelector((state: RootState) => state.requisitions.error);

  // State for rejection modal
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isHistoryVisible, setIsHistoryVisible] = useState(false); // State for history visibility
  const [isCommentSectionVisible, setIsCommentSectionVisible] = useState(false); // State for comment section visibility
  const [newComment, setNewComment] = useState(''); // State for the new comment text
  const [isSubmittingComment, setIsSubmittingComment] = useState(false); // State for comment submission loading

  // State for image modal
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // State to track expanded/collapsed job cards
  const [expandedJobCards, setExpandedJobCards] = useState<{ [key: number]: boolean }>({});

  // State for collapsible quotation
  const [isQuotationOpen, setIsQuotationOpen] = useState(false);

  // State for edit ATR modal
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchRequisitionDetails(id));
    }
    return () => {
      dispatch(clearSelectedRequisition());
      dispatch(clearRequisitionError());
    };
  }, [dispatch, id]);

  const handleApprove = async () => {
    if (!id) return;
    try {
      await dispatch(approveRequisitionAction(id)).unwrap();
      toast.success('Requisition approved successfully');
      navigate('/requisitions');
    } catch (err: any) {
      toast.error(err || 'Failed to approve requisition');
    }
  };

  const openRejectModal = () => {
    setIsRejectModalOpen(true);
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection.');
      return;
    }
    if (!id) return;
    
    try {
      await dispatch(rejectRequisitionAction({ id, data: { comment: rejectionReason } })).unwrap();
      toast.success('Requisition rejected successfully');
      setIsRejectModalOpen(false);
      setRejectionReason('');
      navigate('/requisitions');
    } catch (err: any) {
      toast.error(err || 'Failed to reject requisition');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Comment cannot be empty.');
      return;
    }
    if (!selectedRequisition || !selectedRequisition.id) {
        toast.error('Requisition ID not found.');
        return;
    }

    setIsSubmittingComment(true);
    try {
      const payload: AddCommentPayload = {
        requisitionId: String(selectedRequisition.id),
        content: newComment
      };
      // Dispatch the new action for adding a comment (reply)
      await dispatch(addRequisitionCommentAction(payload)).unwrap();

      setNewComment('');
      toast.success('Comment added!');
      // The slice now handles updating selectedRequisition.replies optimistically.
      // Re-fetching details might not be necessary unless there are other side effects
      // or if you prefer to always have the very latest state from the server.
      // If you re-fetch, ensure it doesn't cause UI jumps if the optimistic update is good.
      // dispatch(fetchRequisitionDetails(String(selectedRequisition.id)));
    } catch (err: any) {
      const errorMessage = err.message || (typeof err === 'string' ? err : 'Failed to add comment.');
      toast.error(errorMessage);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  // Handler for edit ATR modal
  const handleEditATR = () => {
    setShowEditModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditModal(false);
  };

  const handleEditSave = (updatedRequisition: RequisitionDetail) => {
    // Update the selected requisition in the store
    dispatch(fetchRequisitionDetails(id!));
    setShowEditModal(false);
    toast.success('ATR updated successfully');
  };

  const getDisplayError = (err: unknown): string | React.ReactNode | null => {
    if (!err) return null;
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (typeof err === 'object' && err !== null && 'message' in err && typeof (err as any).message === 'string') {
      return (err as any).message;
    }
    return 'An unknown error occurred.';
  };
  const displayError = getDisplayError(error);

  // Conditional rendering for loading and error states BEFORE trying to process 'selectedRequisition'
  if (isLoading && !selectedRequisition && !displayError) {
    return <div className="text-center p-10 dark:text-gray-300">Loading requisition details...</div>;
  }

  if (displayError && !selectedRequisition) {
    return <div className="text-center p-10 text-red-500 dark:text-red-400">Error: {displayError}</div>;
  }

  // If after loading and error checks, selectedRequisition is still null/undefined, show not found.
  // This is the main guard for selectedRequisition before its properties are accessed.
  if (!selectedRequisition) {
    return (
      <div className="text-center p-10 dark:text-gray-300">
        Requisition not found or ID is missing.
        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
          Go Back
        </button>
      </div>
    );
  }

  // From this point, selectedRequisition is guaranteed to be of type RequisitionDetail
  const requisition: RequisitionDetail = selectedRequisition;

  // Gather all job cards (array or single, from all possible keys)
  const collectJobCards = (req: any): any[] => {
    const cards: any[] = [];
    const pushIfArray = (val: any) => { if (Array.isArray(val)) cards.push(...val); else if (val) cards.push(val); };
    pushIfArray(req.jobCards);
    pushIfArray(req.jobcards);
    pushIfArray(req.job_cards);
    pushIfArray(req.jobCard);
    pushIfArray(req.job_card);
    if (req.order) {
      pushIfArray(req.order.jobCards);
      pushIfArray(req.order.jobcards);
      pushIfArray(req.order.job_cards);
    }
    return cards;
  };
  const jobCards = collectJobCards(requisition);

  const currentStatusInfo: StatusInfo | null = getStatusDisplay(requisition);

  const isRequisitionRejectedByAny = requisition.approvers?.some(app => app.status_id === 3);
  const isRequisitionFinalized = (requisition.status_id === 2 || requisition.status_id === 3 || requisition.status_id === 4);

  // Use the same logic as RequisitionListPage to find the next pending approver
  const nextPendingApprover = requisition.approvers?.find(app => app.status_id === 1);
  const isCurrentUserNextApprover = currentUser &&
                                    nextPendingApprover &&
                                    nextPendingApprover.user_id === currentUser.id;

  const canTakeAction =
    Boolean(requisition.submitted) &&
    requisition.status_id === 1 &&
    isCurrentUserNextApprover &&
    !isRequisitionRejectedByAny &&
    !isRequisitionFinalized;

  // Coaches (Area=2, Regional=3) can edit before approving when they are the next pending approver
  const isAreaOrRegionalCoach = currentUser && (currentUser.user_role_id === 2 || currentUser.user_role_id === 3);
  const canEditBeforeApproval = isAreaOrRegionalCoach && requisition.status_id === 1 && isCurrentUserNextApprover;

  const isUserAnApprover = requisition.approvers?.some(app => app.user_id === currentUser?.id);

  // Handler to open modal at a specific image
  const openImageModal = (idx: number) => {
    setModalImageIndex(idx);
    setIsImageModalOpen(true);
  };
  // Handler to close modal
  const closeImageModal = () => setIsImageModalOpen(false);
  // Handlers for next/prev
  const showPrevImage = (pictures: PictureItem[]) => setModalImageIndex((prev) => (prev === 0 ? pictures.length - 1 : prev - 1));
  const showNextImage = (pictures: PictureItem[]) => setModalImageIndex((prev) => (prev === pictures.length - 1 ? 0 : prev + 1));

  const toggleJobCard = (idx: number) => {
    setExpandedJobCards(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8 max-w-5xl">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 transition-colors"
      >
        &larr; Back to List
      </button>

      {displayError && (
        <div className="my-4 p-3 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 rounded-md shadow text-center">
           Error: {displayError}
        </div>
      )}
      <div className="bg-white dark:bg-gray-800 shadow-xl rounded-lg overflow-hidden">
        <div className={`p-4 md:p-6 border-b border-gray-200 dark:border-gray-700 ${currentStatusInfo?.badgeColor?.replace('bg-', 'border-l-4 border-') || 'border-l-4 border-gray-500'}`}>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">Requisition #{requisition.id}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Submitted: {new Date(requisition.date).toLocaleDateString()}</p>
            </div>
            <div className="flex items-center gap-2 mt-2 sm:mt-0">
            {canEditBeforeApproval && (
              <button
                onClick={handleEditATR}
                className="px-3 py-1.5 text-sm font-semibold rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                title="Edit ATR before approving"
              >
                Edit ATR
              </button>
            )}
            {currentStatusInfo && (
              <span
                className={`mt-2 sm:mt-0 px-3 py-1 text-sm font-semibold rounded-full
                  ${requisition.status_id === 2 ? 'bg-green-600 text-white dark:bg-green-600 dark:text-white' : ''}
                  ${requisition.status_id === 3 ? 'bg-red-600 text-white dark:bg-red-600 dark:text-white' : ''}
                  ${requisition.status_id === 1 ? 'bg-gray-600 text-white dark:bg-gray-600 dark:text-white' : ''}
                  ${requisition.status_id === 4 ? 'bg-gray-600 text-white dark:bg-gray-600 dark:text-white' : ''}
                  ${![1,2,3,4].includes(requisition.status_id) ? 'bg-gray-500 text-white dark:bg-gray-600 dark:text-white' : ''}
                `}
                // Always use strong, high-contrast colors for status badge at the top
              >
                {currentStatusInfo.statusText}
              </span>
            )}
            </div>
          </div>
        </div>

        {isUserAnApprover && canTakeAction && ( // Only show buttons if user is an approver AND can take action
          <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleApprove}
              disabled={isLoading}
              className={`w-full sm:w-auto text-white font-bold py-2 px-4 rounded transition-colors duration-150 ease-in-out flex items-center justify-center
                          bg-green-500 hover:bg-green-600 shadow-sm
                          ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Approving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                  </svg>
                  Approve
                </>
              )}
            </button>
            <button
              onClick={openRejectModal}
              disabled={isLoading}
              className={`w-full sm:w-auto text-white font-bold py-2 px-4 rounded transition-colors duration-150 ease-in-out flex items-center justify-center
                          bg-red-500 hover:bg-red-600 shadow-sm
                          ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                  </svg>
                  Reject
                </>
              )}
            </button>
          </div>
        )}

        {isRejectModalOpen && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
            <div className="bg-white dark:bg-gray-800 p-5 md:p-6 rounded-lg shadow-xl w-full max-w-md">
              <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">Reject Requisition #{requisition.id}</h3>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Enter rejection reason (required)"
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500 h-28"
                rows={4}
              />
              <div className="mt-5 flex justify-end space-x-3">
                <button
                  onClick={() => setIsRejectModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={isLoading || !rejectionReason.trim()}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md disabled:opacity-50 transition-colors"
                >
                  {isLoading ? 'Submitting...' : 'Submit Rejection'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 md:p-6">
          {/* Adjust grid columns for different screen sizes */}
          {/* Small screens: 1 column (implicit) */}
          {/* Medium screens: 3 columns (Key Info: 1, Approval Chain: 2) */}
          {/* Large screens: 2 columns (Key Info: 1, Approval Chain: 1) */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 gap-x-6 gap-y-4 md:gap-y-6">
            <div className="md:col-span-1 lg:col-span-1 space-y-3">
              <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-2 dark:border-gray-600">ATR Information</h4>
              <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm">
                <ul className="space-y-2">
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Store:</span> {requisition.stores?.map(s => `${s.name} (${s.storenumber})`).join(', ') || 'N/A'}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Category:</span> {requisition.category?.name || ((requisition as any).requisition_category_id === 1 ? 'Pots/Fryers/Toasters' : 'N/A')}{requisition.subcategory?.name ? ` - ${requisition.subcategory.name}` : ((requisition as any).requisition_sub_category_id === 19 ? ' - Toaster' : '')}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span> <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200">{(requisition as any).is_repair === 1 ? 'Repair' : 'New Purchase'}</span>
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Created By:</span> {requisition.user?.name || 'N/A'}
                  </li>
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Last Updated:</span> {new Date(requisition.updated_at).toLocaleString()}
                  </li>
                  {currentStatusInfo && requisition.status_id === 1 && requisition.approvers && requisition.approvers.length > 0 && (
                    <li className="pt-1 border-t border-gray-100 dark:border-gray-700">
                      <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200">
                        Next Approver: {requisition.approvers.find(app => app.status_id === 1)?.user.name || 'N/A'}
                      </span>
                    </li>
                  )}
                  {currentStatusInfo && requisition.status_id === 3 && (
                    <li className="pt-1 border-t border-gray-100 dark:border-gray-700">
                      <span className="px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-200">
                        Reason: {requisition.approvers?.find(app => app.status_id === 3)?.comment || requisition.histories?.find(h => h.action === 'Rejected' && h.comment)?.comment || 'Rejection reason not specified'}
                      </span>
                    </li>
                  )}
                  {requisition.order && (
                    <li className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Order/Ref #:</span> {requisition.order.order_number || requisition.order.reference_number || 'N/A'}
                    </li>
                  )}
                  <li className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Internal:</span> {requisition.internal ? 'Yes' : 'No'}
                  </li>
                  {requisition.internal === 1 ? (
                    <li className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Technician:</span> {requisition.technician?.name || 'N/A'}
                    </li>
                  ) : (
                    <li className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">Supplier:</span> {requisition.supplier?.name || 'N/A'}
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <div className="md:col-span-2 lg:col-span-1 space-y-3">
              <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-2 dark:border-gray-600">Approval Chain</h4>
              {requisition.approvers && requisition.approvers.length > 0 ? (
                <ul className="space-y-2">
                  {requisition.approvers.map((approver, index) => {
                    const approverStatusDisplay = getStatusDisplay({ status_id: approver.status_id, submitted: true });

                    // Apply stronger, high-contrast versions of status colors for both light and dark mode
                    const bgColorClass = (() => {
                      if (approver.status_id === 1) return 'bg-gray-600 text-white dark:bg-gray-600 dark:text-white'; // Pending
                      if (approver.status_id === 2) return 'bg-green-600 text-white dark:bg-green-600 dark:text-white'; // Approved
                      if (approver.status_id === 3) return 'bg-red-600 text-white dark:bg-red-600 dark:text-white'; // Rejected
                      return 'bg-gray-600 text-white dark:bg-gray-600 dark:text-white'; // Default
                    })();

                    return (
                      <li
                        key={approver.id || index}
                        className={`p-2.5 border rounded-md shadow-sm transition-colors ${bgColorClass} ${
                          approver.status_id === 1 ? 'border-blue-200 dark:border-blue-700' :
                          approver.status_id === 2 ? 'border-green-200 dark:border-green-700' :
                          approver.status_id === 3 ? 'border-red-200 dark:border-red-700' :
                          'border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            {/* Approver name: always white for contrast on strong bg */}
                            <span className="font-medium text-sm text-white">{approver.user.name}</span>
                                            {approver.status_id === 3 && approver.comment && (
                  <p className="text-xs text-red-200 dark:text-red-100 mt-0.5">Reason: {approver.comment}</p>
                )}
                          </div>
                          {/* Status badge: always white text for strong contrast */}
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full bg-white bg-opacity-10 text-white`}> {/* Use a subtle white overlay for badge */}
                            {approverStatusDisplay.statusText}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No approvers assigned or approval chain not available.</p>
              )}
            </div>

            <div className="md:col-span-3 lg:col-span-2 space-y-4"> {/* Items/History/Comments/Attachments section takes full width on md, and full width (2 cols) on lg */}
                <div>
                    <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Items to Repair / Request</h4>
                    {(requisition.equipmentItems && requisition.equipmentItems.length > 0) || (requisition.assetDescriptionPairs && requisition.assetDescriptionPairs.length > 0) || (requisition.assets && requisition.assets.length > 0) || (requisition.equipmentRequests && requisition.equipmentRequests.length > 0) ? (
                        <div className="space-y-3">
                            {(() => {
                                const items = [];

                                // First, use processed equipment items from Laravel (matches Livewire exactly)
                                if (requisition.equipmentItems && requisition.equipmentItems.length > 0) {
                                    requisition.equipmentItems.forEach((item, index) => {
                                        items.push(
                                            <div
                                                key={`processed-eq-${index}`}
                                                className="p-2.5 border border-amber-200 dark:border-amber-700 rounded-md bg-amber-100 dark:bg-amber-900/70 shadow-sm"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                        <strong>{item.asset}</strong><br />
                                                        <small>Category: {item.equipment.category} Subcategory: {item.equipment.subcategory}</small>
                                                    </p>
                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                                        Equipment
                                                    </span>
                                                </div>
                                                <div className="mt-1 border-t border-amber-100 dark:border-amber-800 pt-1">
                                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                                        <span className="font-medium">Serial Number:</span> {item.equipment.serial_number}
                                                    </p>
                                                    {item.descriptions && item.descriptions.length > 0 && (
                                                        <div className="mt-1">
                                                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                                                <span className="font-medium">Repair Description:</span>
                                                            </p>
                                                            <ul className="list-disc list-inside ml-2 text-xs text-gray-600 dark:text-gray-400">
                                                                {item.descriptions.map((desc, dIndex) => (
                                                                    desc && <li key={dIndex}>{nl2br(desc)}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                }

                                // Second, use processed asset description pairs from Laravel
                                if (requisition.assetDescriptionPairs && requisition.assetDescriptionPairs.length > 0) {
                                    requisition.assetDescriptionPairs.forEach((pair, index) => {
                                        items.push(
                                            <div
                                                key={`processed-asset-${index}`}
                                                className="p-2.5 border border-indigo-200 dark:border-indigo-700 rounded-md bg-indigo-100 dark:bg-indigo-900/70 shadow-sm"
                                            >
                                                <div className="flex items-start justify-between">
                                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                        Asset: {pair.asset}
                                                    </p>
                                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
                                                        Asset
                                                    </span>
                                                </div>
                                                <div className="mt-1 border-t border-indigo-100 dark:border-indigo-800 pt-1">
                                                    {pair.descriptions && pair.descriptions.length > 0 ? (
                                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                                            <span className="font-medium">Description:</span> {nl2br(pair.descriptions.join('\n'))}
                                                        </p>
                                                    ) : (
                                                        <p className="text-xs text-gray-600 dark:text-gray-400">
                                                            <span className="font-medium">Description:</span> <em>No description</em>
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    });
                                }

                                // Fallback to legacy data only if no processed data is available
                                if (items.length === 0) {
                                    // Track displayed descriptions to prevent duplicates
                                    const displayedDescriptions = new Set<string>();

                                    // Render equipment requests
                                    const equipmentItems = requisition.equipmentRequests?.map((item, index) => (
                                                                            <div
                                            key={`eq-${item.id || index}`}
                                            className="p-2.5 border border-amber-200 dark:border-amber-700 rounded-md bg-amber-100 dark:bg-amber-900/70 shadow-sm"
                                        >
                                            <div className="flex items-start justify-between">
                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                    {item.equipment?.description || 'Equipment Item'}
                                                    {item.equipment?.serial_number && (
                                                        <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                                                            (S/N: {item.equipment.serial_number})
                                                        </span>
                                                    )}
                                                </p>
                                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                                    Equipment
                                                </span>
                                            </div>
                                            {item.notes && (
                                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 border-t border-amber-100 dark:border-amber-800 pt-1">
                                                    <span className="font-medium">Notes:</span> {nl2br(item.notes)}
                                                </p>
                                            )}
                                        </div>
                                    )) || [];

                                    // Render asset items with their descriptions
                                    const assetItems = requisition.assets?.map((item, index) => {
                                        // Add any available descriptions to our tracking set
                                        if (item.descriptions) {
                                            item.descriptions.forEach(desc => {
                                                if (desc.description) {
                                                    displayedDescriptions.add(desc.description);
                                                }
                                            });
                                        }

                                        return (
                                                                                    <div
                                            key={`asset-${item.id || index}`}
                                            className="p-2.5 border border-indigo-200 dark:border-indigo-700 rounded-md bg-indigo-100 dark:bg-indigo-900/70 shadow-sm"
                                        >
                                            <div className="flex items-start justify-between">
                                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                    Asset: {item.asset}
                                                </p>
                                            </div>
                                            <div className="mt-1 border-t border-indigo-100 dark:border-indigo-800 pt-1 space-y-1">
                                                {item.descriptions?.map((desc, dIndex) => (
                                                    <p key={`desc-${desc.id || dIndex}`} className="text-md text-gray-600 dark:text-gray-400">
                                                        <span className="font-medium">Description:</span> {nl2br(desc.description)}
                                                    </p>
                                                ))}
                                                {(!item.descriptions || item.descriptions.length === 0) && requisition.repairs && requisition.repairs[index] && !displayedDescriptions.has(requisition.repairs[index].description) && (
                                                    <p className="text-md text-gray-600 dark:text-gray-400">
                                                        <span className="font-medium">Description:</span> {nl2br(requisition.repairs[index].description)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }) || [];

                                // Render legacy repairs that haven't been displayed yet
                                const legacyItems = requisition.assets?.length === 0 && requisition.repairs && requisition.repairs.length > 0
                                    ? requisition.repairs.map((repair, index) => {
                                        // Skip if this description was already shown
                                        if (displayedDescriptions.has(repair.description)) {
                                            return null;
                                        }

                                        return (
                                                                                            <div
                                                    key={`legacy-repair-${repair.id || index}`}
                                                    className="p-2.5 border border-purple-200 dark:border-purple-700 rounded-md bg-purple-100 dark:bg-purple-900/70 shadow-sm"
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                                            Item {index + 1}
                                                        </p>
                                                        <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
                                                            Legacy
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 border-t border-purple-100 dark:border-purple-800 pt-1">
                                                        <span className="font-medium">Description:</span> {repair.description}
                                                    </p>
                                                </div>
                                            );
                                        }).filter(Boolean as any)
                                        : [];

                                    items.push(...equipmentItems, ...assetItems, ...legacyItems);
                                }

                                return items;
                            })()}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No items listed for repair or request.</p>
                    )}
                </div>

                <div className="mt-6">
                  <div className="p-3 border border-green-200 dark:border-green-700 rounded-md bg-green-50 dark:bg-green-900/70 shadow-sm">
                    {requisition.quotation ? (
                      <>
                        <button
                          type="button"
                          className="flex items-center w-full justify-between text-left text-green-700 dark:text-green-200 font-semibold focus:outline-none"
                          onClick={() => setIsQuotationOpen((prev) => !prev)}
                        >
                          <div className="flex flex-wrap gap-x-6 gap-y-1 items-center w-full">
                            {(requisition.quotation as any)?.quotation_number && (
                              <span className="text-sm font-medium">Quotation #: <span className="font-semibold">{(requisition.quotation as any).quotation_number}</span></span>
                            )}
                            {(requisition.quotation as any)?.date && (
                              <span className="text-sm font-medium">Date: <span className="font-normal">{new Date((requisition.quotation as any).date).toLocaleDateString('en-ZA')}</span></span>
                            )}
                            <span className="text-sm font-medium">Amount: <span className="text-lg font-bold text-green-600 dark:text-green-400">{formatCurrency(requisition.quotation.amount)}</span></span>
                            {(requisition.quotation as any)?.vat !== undefined && (
                              <span className="text-sm font-medium">VAT: <span className="font-normal">{(requisition.quotation as any).vat === 1 ? 'Inclusive' : 'Exclusive'}</span></span>
                            )}
                          </div>
                          <svg
                            className={`w-5 h-5 ml-2 transition-transform ${isQuotationOpen ? 'rotate-90' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        {isQuotationOpen && (
                          <div className="mt-3 space-y-3">
                            {(requisition.quotation as any)?.attachment && (
                              <div className="flex justify-end">
                                <a
                                  href={`${process.env.REACT_APP_BACKEND_URL}/quote-file/${(requisition.quotation as any).attachment}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                >
                                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                  View Quote
                                </a>
                              </div>
                            )}
                            {(requisition.quotation as any)?.attachment && (
                              <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
                                <iframe
                                  src={`${process.env.REACT_APP_BACKEND_URL}/quote-file/${(requisition.quotation as any).attachment}`}
                                  className="w-full h-96"
                                  title="Quote Document"
                                  onError={(e) => {
                                    // Hide iframe if document can't be loaded
                                    (e.target as HTMLIFrameElement).style.display = 'none';
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No quotation available</p>
                    )}
                  </div>
                </div>

                {/* Job Card Section - supports multiple job cards */}
                <div className="mt-6">
                  <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 dark:border-gray-600">Job Card{jobCards.length > 1 ? 's' : ''}</h4>
                  {jobCards.length > 0 ? (
                    <div className="space-y-4">
                      {jobCards.map((jobCard, idx) => (
                        <div key={jobCard.id || idx} className="p-3 border border-indigo-200 dark:border-indigo-700 rounded-md bg-indigo-50 dark:bg-indigo-900/70 shadow-sm">
                          <button
                            type="button"
                            className="flex items-center w-full justify-between text-left text-indigo-700 dark:text-indigo-200 font-semibold focus:outline-none"
                            onClick={() => toggleJobCard(idx)}
                          >
                            <span>Job Card #{jobCard.job_number || idx + 1}</span>
                            <svg
                              className={`w-5 h-5 ml-2 transition-transform ${expandedJobCards[idx] ? 'rotate-90' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                          {expandedJobCards[idx] && (
                            <div className="space-y-2 mt-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Job Card #:</span>
                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{jobCard.job_number || 'N/A'}</span>
                              </div>
                              {jobCard.created_at && (
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Date:</span>
                                  <span className="text-sm text-gray-900 dark:text-gray-100">{new Date(jobCard.created_at).toLocaleDateString('en-ZA')}</span>
                                </div>
                              )}
                              {jobCard.attachment && (
                                <div className="flex justify-end mt-2">
                                  <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/job-card-file/${jobCard.attachment}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                  >
                                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    View Job Card
                                  </a>
                                </div>
                              )}
                              {jobCard.attachment && (
                                <div className="border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden mt-2">
                                  <iframe
                                    src={`${process.env.REACT_APP_BACKEND_URL}/job-card-file/${jobCard.attachment}`}
                                    className="w-full h-96"
                                    title={`Job Card Document ${jobCard.job_number || idx + 1}`}
                                    onError={(e) => {
                                      (e.target as HTMLIFrameElement).style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700">
                      <p className="text-sm text-gray-500 dark:text-gray-400">No job card available</p>
                    </div>
                  )}
                </div>

                <div className="mt-6">
                    <div className="flex justify-between items-center border-b pb-1 mb-3 dark:border-gray-600">
                        <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300">
                            Comments
                            {selectedRequisition && selectedRequisition.replies && (
                                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                                    ({selectedRequisition.replies.length})
                                </span>
                            )}
                        </h4>
                        {!isRequisitionFinalized && (
                            <button
                                onClick={() => setIsCommentSectionVisible(!isCommentSectionVisible)}
                                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            >
                                {isCommentSectionVisible ? 'Hide Form' : 'Add Comment'}
                            </button>
                        )}
                    </div>

                    {isCommentSectionVisible && !isRequisitionFinalized && (
                        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-sm p-3 mb-4">
                            <textarea
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                placeholder="Type your comment here..."
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:ring-blue-500 focus:border-blue-500 h-24 resize-none"
                                rows={3}
                                disabled={isSubmittingComment}
                            />
                            <div className="flex justify-end mt-2">
                                <button
                                    onClick={handleAddComment}
                                    disabled={isSubmittingComment || !newComment.trim()}
                                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md disabled:opacity-50 transition-colors flex items-center justify-center shadow-sm"
                                >
                                    {isSubmittingComment ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd"></path>
                                            </svg>
                                            Add Comment
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {selectedRequisition && selectedRequisition.replies && selectedRequisition.replies.length > 0 ? (
                        <div className="space-y-3">
                            {selectedRequisition.replies
                                .slice()
                                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                                .map((reply) => (
                                    <div
                                        key={reply.id || reply.created_at}
                                        className="p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-100 dark:bg-gray-700 shadow-sm"
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                                                {reply.user?.name || 'Unknown User'}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                                                {new Date(reply.created_at).toLocaleString()}
                                            </span>
                                        </div>
                                        {reply.content && (
                                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                                {reply.content}
                                            </p>
                                        )}
                                    </div>
                                ))}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {isRequisitionFinalized ? 'No comments for this requisition.' : 'No comments yet. Be the first to add one!'}
                        </p>
                    )}
                </div>

                <div>
                    <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 border-b pb-1 mb-3 mt-6 dark:border-gray-600">Attachments</h4>
                    {requisition.pictures && requisition.pictures.length > 0 ? (
                        <>
                        {/* Responsive grid of image thumbnails */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {requisition.pictures.map((pic, idx) => {
                                // Use CRA env var for image URL construction
                                const imageUrl = pic.attachment ? `${process.env.REACT_APP_BACKEND_URL}/requisition-image/${pic.attachment}` : 'https://placehold.co/150x150?text=No+Image';
                                // Thumbnail URL logging removed
                                return (
                                    <div key={pic.id ?? idx} className="relative group cursor-pointer" onClick={() => openImageModal(idx)}>
                                        <img
                                            src={imageUrl}
                                            alt={pic.comment || `Attachment ${pic.id ?? idx}`}
                                            className="w-full h-32 object-cover rounded-md border dark:border-gray-600 group-hover:opacity-80 transition-opacity"
                                            onError={(e) => (e.currentTarget.src = 'https://placehold.co/150x150?text=No+Image')}
                                        />
                                        {/* Overlay for comment */}
                                        {pic.comment && (
                                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-1 rounded-b-md truncate">
                                                {pic.comment}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {/* Modal/Lightbox for full image view with next/prev */}
                        {isImageModalOpen && requisition.pictures && requisition.pictures.length > 0 && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 backdrop-blur-sm">
                                <button
                                    className="absolute top-4 right-4 text-white text-2xl font-bold bg-black bg-opacity-40 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70"
                                    onClick={closeImageModal}
                                    aria-label="Close"
                                >
                                    &times;
                                </button>
                                <button
                                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-3xl font-bold bg-black bg-opacity-40 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70"
                                    onClick={() => showPrevImage(requisition.pictures || [])}
                                    aria-label="Previous image"
                                >
                                    &#8592;
                                </button>
                                <div className="max-w-full max-h-[80vh] flex flex-col items-center">
                                    {
                                        (() => {
                                            const currentPic = requisition.pictures[modalImageIndex];
                                            // Use CRA env var for modal image URL construction
                                            const modalImageUrl = currentPic.attachment ? `${process.env.REACT_APP_BACKEND_URL}/requisition-image/${currentPic.attachment}` : 'https://placehold.co/600x400?text=No+Image';
                                            // Modal image URL logging removed
                                            return (
                                                <>
                                                    <img
                                                        src={modalImageUrl}
                                                        alt={currentPic.comment || `Attachment ${currentPic.id ?? modalImageIndex}`}
                                                        className="rounded-lg shadow-lg max-h-[70vh] max-w-[90vw] object-contain"
                                                    />
                                                    {currentPic.comment && (
                                                        <div className="mt-2 text-white text-center text-sm bg-black bg-opacity-60 px-3 py-1 rounded">
                                                            {currentPic.comment}
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()
                                    }
                                </div>
                                <button
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-3xl font-bold bg-black bg-opacity-40 rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-70"
                                    onClick={() => showNextImage(requisition.pictures || [])}
                                    aria-label="Next image"
                                >
                                    &#8594;
                                </button>
                            </div>
                        )}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">No pictures attached...</p>
                    )}
                </div>

                <div>
                    <div className="flex justify-between items-center border-b pb-1 mb-3 dark:border-gray-600">
                        <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300">
                            History
                            {requisition.histories && (
                                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                                    ({requisition.histories.length})
                                </span>
                            )}
                        </h4>
                        <button
                            onClick={() => setIsHistoryVisible(!isHistoryVisible)}
                            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                            {isHistoryVisible ? 'Hide' : 'Show'}
                        </button>
                    </div>
                    {isHistoryVisible && requisition.histories && requisition.histories.length > 0 ? (
                        <ul className="space-y-2 mt-2 max-h-96 overflow-y-auto pr-2">
                        {requisition.histories.slice().sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((entry) => {
                            // Determine styles based on action type
                            const actionStyles = (() => {
                                switch(entry.action) {
                                    case 'Approved':
                                        return {
                                            bgColor: 'bg-green-100 dark:bg-green-900/70',
                                            borderColor: 'border-green-200 dark:border-green-700',
                                            badgeColor: 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                                        };
                                    case 'Rejected':
                                        return {
                                            bgColor: 'bg-red-100 dark:bg-red-900/70',
                                            borderColor: 'border-red-200 dark:border-red-700',
                                            badgeColor: 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200'
                                        };
                                    case 'Submitted':
                                        return {
                                            bgColor: 'bg-blue-100 dark:bg-blue-900/70',
                                            borderColor: 'border-blue-200 dark:border-blue-700',
                                            badgeColor: 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'
                                        };
                                    case 'Comment':
                                        return {
                                            bgColor: 'bg-yellow-100 dark:bg-yellow-900/70',
                                            borderColor: 'border-yellow-200 dark:border-yellow-700',
                                            badgeColor: 'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                                        };
                                    default:
                                        return {
                                            bgColor: 'bg-gray-100 dark:bg-gray-800',
                                            borderColor: 'border-gray-200 dark:border-gray-700',
                                            badgeColor: 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        };
                                }
                            })();

                            return (
                                <li
                                    key={entry.id || entry.created_at}
                                    className={`p-2.5 border rounded-md shadow-sm ${actionStyles.bgColor} ${actionStyles.borderColor}`}
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-start gap-2">
                                            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${actionStyles.badgeColor}`}>
                                                {entry.action}
                                            </span>
                                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                                {entry.user?.name || 'System'}
                                            </span>
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap ml-2">
                                            {new Date(entry.created_at).toLocaleString()}
                                        </span>
                                    </div>
                                    {entry.comment && (
                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1.5 border-t pt-1.5 whitespace-pre-wrap">
                                            {entry.comment}
                                        </p>
                                    )}
                                </li>
                            );
                        })}
                        </ul>
                    ) : isHistoryVisible && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">No history entries for this requisition.</p>
                    )}
                </div>
            </div>

          </div>
        </div>
      </div>

      {/* Edit ATR Modal */}
      {showEditModal && (
        <EditATRModal
          requisitionId={requisition?.id || null}
          onClose={handleEditModalClose}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default RequisitionDetailPage;
